/**
 * Shared utilities for converting and fetching venue branding configuration.
 *
 * `toThemeConfig` is the single source of truth for mapping
 * BrandingConfigSerializer -> LayoutThemeConfig.  It is used in:
 *   - SSR (withEventDetail, withMembershipDetail, withWebsiteDetail)
 *   - Client hooks (useWebsiteBrandingTheme, BrandedCheckoutPage)
 *   - createLandingPageComponent getThemeConfig callbacks
 *
 * Fonts are excluded by default (events, memberships, checkout).
 * Pass `{ includeFonts: true }` for website pages / Puck editor.
 */

import { QueryClient } from "react-query";
import { QueryKeyManager } from "@shared/services/api/hooks/utils/queryKeyManager";
import type { LayoutThemeConfig } from "@shared/config/layout.config";
import PublicBrandingConfigRepository from "../data/repositories/PublicBrandingConfigRepository";
import type { BrandingConfigSerializer } from "../data/types";

/**
 * Generates the React Query cache key used by all branding operations.
 * Shared across SSR prefetch, client hooks, and cache reads.
 */
export function getBrandingQueryKey(): string[] {
  const repo = new PublicBrandingConfigRepository();
  const queryKeyManager = new QueryKeyManager(repo);
  return queryKeyManager.generateKey(["retrieve", "resolve"]);
}

export type BrandingModelType =
  | "event"
  | "venue"
  | "membership_group"
  | "website";

export interface FetchBrandingConfigParams {
  modelType: BrandingModelType;
  instanceId: number;
}

/**
 * Fetches venue branding configuration and stores in React Query cache.
 * Returns the branding data for immediate use in SSR props.
 *
 * @param queryClient - React Query client for caching
 * @param params - Model type and instance ID for the branding lookup
 * @returns Branding config or null if not found/error
 */
export async function fetchBrandingConfig(
  queryClient: QueryClient,
  params: FetchBrandingConfigParams
): Promise<BrandingConfigSerializer | null> {
  const { modelType, instanceId } = params;

  if (!instanceId) {
    return null;
  }

  try {
    const brandingRepo = new PublicBrandingConfigRepository();
    const queryKey = getBrandingQueryKey();

    const requestParams = {
      model_type: modelType,
      instance_id: instanceId,
    };

    // Fetch the branding data
    const brandingData = await brandingRepo.retrieve("resolve", {
      params: requestParams,
    });

    // Use setQueryData to overwrite any existing cache (instead of fetchQuery which won't overwrite)
    queryClient.setQueryData(queryKey, brandingData);

    return brandingData;
  } catch (e) {
    // Branding is optional - don't break the page if it fails
    // eslint-disable-next-line no-console
    console.error("[SSR Branding] Failed to fetch:", e);
    return null;
  }
}

export interface ToThemeConfigOptions {
  /** Include custom fonts in the theme config. Only website pages use fonts. */
  includeFonts?: boolean;
}

/**
 * Single source of truth for converting BrandingConfigSerializer to LayoutThemeConfig.
 *
 * Fonts are excluded by default because most page types (events, memberships,
 * checkout) use Showpass default fonts.  Website pages and the Puck editor
 * pass `{ includeFonts: true }`.
 */
export function toThemeConfig(
  brandingConfig: BrandingConfigSerializer | null | undefined,
  options?: ToThemeConfigOptions
): LayoutThemeConfig | undefined {
  if (!brandingConfig?.has_branding_config) {
    return undefined;
  }

  const colorMode = brandingConfig.branding_color_mode as
    | "light"
    | "dark"
    | null;

  return {
    colorMode: colorMode ?? "light",
    forceColorMode: Boolean(colorMode),
    primaryColor: brandingConfig.branding_primary_color ?? undefined,
    secondaryColor: brandingConfig.branding_secondary_color ?? undefined,
    logoImage: brandingConfig.branding_logo ?? undefined,
    logoLinkUrl: brandingConfig.branding_website_url ?? undefined,
    ...(options?.includeFonts
      ? {
          headingFont: brandingConfig.branding_heading_font ?? undefined,
          bodyFont: brandingConfig.branding_body_font ?? undefined,
        }
      : {}),
  };
}

/**
 * Helper to fetch branding and convert to theme config in one call.
 */
export async function fetchAndConvertBrandingConfig(
  queryClient: QueryClient,
  params: FetchBrandingConfigParams,
  themeOptions?: ToThemeConfigOptions
): Promise<{
  brandingConfig: BrandingConfigSerializer | null;
  themeConfig: LayoutThemeConfig | undefined;
}> {
  const brandingConfig = await fetchBrandingConfig(queryClient, params);
  const themeConfig = toThemeConfig(brandingConfig, themeOptions);

  return { brandingConfig, themeConfig };
}
